import api from './apiClient';
import { ClinicSummary, PaginatedPetClinicSummary, PetClinicSummary } from '../types';

export const adminService = {
  getStatistics: async (): Promise<{
    total_users: number;
    active_users: number;
    total_pets: number;
    active_pets: number;
    total_medical_controls: number;
    total_vaccinations: number;
    today_activity: number;
    weekly_activity: number;
    recent_medical_controls: number;
    users_by_role: Record<string, number>;
    generated_at: string;
  }> => {
    const response = await api.get('/admin/statistics');
    return response.data;
  },

  getRecentActivity: async (limit: number = 20): Promise<{
    id: number;
    action: string;
    user_id: number;
    user_email: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    details: string;
  }[]> => {
    const response = await api.get(`/admin/recent-activity?limit=${limit}`);
    return response.data;
  },

  getUsersSummary: async (): Promise<{
    recent_users: number;
    pending_users: number;
    verified_veterinarians: number;
    total_users: number;
    active_users: number;
  }> => {
    const response = await api.get('/admin/users/summary');
    return response.data;
  },
  
  getClinicsList: async (): Promise<ClinicSummary[]> => {
    const response = await api.get('/clinics/', { params: { limit: 200 } });
    return response.data;
  },
  
  getPetClinicAssignments: async (
    params: { search?: string; limit?: number; page?: number } = {}
  ): Promise<PaginatedPetClinicSummary> => {
    const response = await api.get('/pets/admin/clinic-assignments', {
      params,
    });
    return response.data;
  },
  
  assignPetToClinic: async (petId: number, clinicId: number | null): Promise<PetClinicSummary> => {
    const response = await api.put(`/pets/admin/${petId}/clinic`, { clinic_id: clinicId });
    return response.data;
  },

  getAnalytics: async (days: number = 30): Promise<{
    daily_data: Array<{
      date: string;
      users: number;
      cumulative_users: number;
      subscriptions: number;
      cumulative_subscriptions: number;
      revenue: number;
      cumulative_revenue: number;
    }>;
    projection_data: Array<{
      date: string;
      users: number;
      cumulative_users: number;
      subscriptions: number;
      cumulative_subscriptions: number;
      revenue: number;
      cumulative_revenue: number;
      is_projection: boolean;
    }>;
    totals: {
      total_users: number;
      total_active_subscriptions: number;
      total_revenue: number;
      average_revenue_per_user: number;
    };
    plans_distribution: Record<string, number>;
    period: {
      start_date: string;
      end_date: string;
      days: number;
    };
  }> => {
    const response = await api.get(`/admin/analytics?days=${days}`);
    return response.data;
  },

  getMaintenanceTables: async (): Promise<{
    tables: Array<{ table_name: string; row_count: number; reason: string }>;
  }> => {
    const response = await api.get('/admin/maintenance/tables');
    return response.data;
  },

  runMaintenance: async (tableNames?: string[]): Promise<{
    completed: string[];
    errors: Array<{ table: string; error: string }>;
    success: boolean;
  }> => {
    const response = await api.post('/admin/maintenance/run', {
      table_names: tableNames ?? [],
    });
    return response.data;
  },

  getLogTables: async (olderThanDays?: number): Promise<{
    tables: Array<{
      table_name: string;
      row_count: number;
      older_than_days: number;
      count_older_than_cutoff: number;
      date_column: string;
      error?: string;
    }>;
    older_than_days: number;
    cutoff_iso?: string;
  }> => {
    const params = olderThanDays != null ? { older_than_days: olderThanDays } : {};
    const response = await api.get('/admin/maintenance/log-tables', { params });
    return response.data;
  },

  purgeLogs: async (olderThanDays?: number): Promise<{
    deleted: Array<{ table: string; deleted: number }>;
    errors: Array<{ table: string; error: string }>;
    vacuum: { completed: string[]; errors: unknown[]; success: boolean };
    success: boolean;
    cutoff_iso?: string;
  }> => {
    const body = olderThanDays != null ? { older_than_days: olderThanDays } : {};
    const response = await api.post('/admin/maintenance/purge-logs', body);
    return response.data;
  },

  getErrorsSummary: async (days: number = 30): Promise<{
    daily_errors: Array<{
      date: string;
      errors: number;
    }>;
    total_errors: number;
    recent_errors: Array<{
      id: number;
      action: string;
      user_email: string;
      details: string;
      created_at: string;
      ip_address: string;
      severity: 'crítico' | 'medio' | 'bajo';
    }>;
    error_types: Record<string, number>;
    errors_by_severity: {
      crítico: number;
      medio: number;
      bajo: number;
    };
    has_critical_errors: boolean;
    period: {
      start_date: string;
      end_date: string;
      days: number;
    };
  }> => {
    const response = await api.get(`/admin/errors?days=${days}`);
    return response.data;
  },
};








